/**
 * The alert status and severity tags, shared by the local and cloud dashboards so the two
 * can't drift into different colours for the same state.
 *
 * Deliberate hierarchy: severity is a SOLID pill (it's the field that should catch the eye
 * when scanning a list of alerts), status is a soft tinted pill with a dot. Previously both
 * were the same soft weight, so a critical alert looked no more urgent than a resolved one.
 */
import StatusTag, { toneVar, type StatusTone } from '../common/StatusTag';

/** Alert lifecycle state → tone. */
const STATUS_TONE: Record<string, StatusTone> = {
  new: 'danger',
  acknowledged: 'warning',
  assigned: 'info',
  resolved: 'success',
  cancelled: 'neutral',
};

/** Severity → tone. */
const SEVERITY_TONE: Record<string, StatusTone> = {
  critical: 'danger',
  warning: 'warning',
  normal: 'info',
};

export function alertStatusTone(status?: string | null): StatusTone {
  return STATUS_TONE[(status || '').toLowerCase()] || 'neutral';
}

export function alertSeverityTone(severity?: string | null): StatusTone {
  return SEVERITY_TONE[(severity || '').toLowerCase()] || 'info';
}

/**
 * The severity colour as a bare CSS var, for the alert list's left-edge stripe — the row
 * carries its urgency before you read a word of it, which a pill in the middle of the row
 * can't do while you're scanning a page of 25.
 */
export function alertSeverityVar(severity?: string | null) {
  return toneVar(alertSeverityTone(severity));
}

/** Where in the lifecycle this alert sits. Soft pill. */
export function AlertStatusTag({ status, className }: { status?: string | null; className?: string }) {
  return (
    <StatusTag tone={alertStatusTone(status)} className={className}>
      {status || 'new'}
    </StatusTag>
  );
}

/**
 * How urgent this alert is. Solid pill by default — the loudest thing in the row.
 *
 * `variant="soft"` is for the surfaces that carry severity some OTHER way as well (the alert
 * list paints it as a stripe down the row's left edge): two solid shouts on one row read as
 * noise, so there the chip steps back to a tint and the stripe does the scanning work.
 */
export function SeverityTag({
  severity,
  className,
  variant = 'solid',
  dot,
}: {
  severity?: string | null;
  className?: string;
  variant?: 'soft' | 'solid';
  dot?: boolean;
}) {
  return (
    <StatusTag tone={alertSeverityTone(severity)} variant={variant} dot={dot} className={className}>
      {severity || 'normal'}
    </StatusTag>
  );
}
